import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  Button,
  Divider,
  Icon,
  Label,
  Menu,
  Message,
  Segment,
  Tab,
  Table,
} from "semantic-ui-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { MultiLineParagraph } from "components/lib/UI";
import BasicAccordion from "components/lib/UI/BasicAccordion";

type IdeaAssessmentReportProps = {
  idea: OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaChallenges"],
    OpenAPI.Schemas["AssignIdeaAssessmentDetails"],
    OpenAPI.Schemas["AssignIdeaTemplates"]
  >;
  challenge?: OpenAPI.Assign<
    OpenAPI.Schemas["Challenge"],
    OpenAPI.Schemas["AssignChallengeIdeaTemplate"]
  >;
  fixedHeight?: number | null;
};

const IdeaAssessmentReport = ({
  idea,
  challenge,
  fixedHeight = 400,
}: IdeaAssessmentReportProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const [selectedAssessor, setSelectedAssessor] = useState("");

  const template = idea.ownerIdeaTemplate || challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];
  const [selectedAssessmentRoundId, setSelectedAssessmentRoundId] = useState(
    assessmentRounds?.at(0)?.id || "legacy",
  );

  // Associate a given assessment ID with its title
  const getAssessmentTitle = (assessmentId: string) => {
    const assessmentSections =
      template?.body?.filter((section) => section.type === "assessment") ?? [];

    const assessmentFields = assessmentSections.reduce(
      (acc, section) => acc.concat(section.fields),
      [] as NonNullable<
        OpenAPI.Schemas["IdeaTemplate"]["body"]
      >[number]["fields"],
    );
    const field = assessmentFields.find((f) => f.id === assessmentId);
    return field?.title || "Unknown field";
  };

  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );

  // Pass array of idea ids and user ids, and remove the users as assessors from these ideas
  const removeAssessors = (
    ideaIds: string[],
    userIds: string[],
    assessmentRoundId: string,
  ) => {
    api.challenges.unassignAssessors(
      idea.challenge,
      ideaIds,
      userIds,
      assessmentRoundId,
      ({ ideas: assessorIdeas }) => {
        api.invalidateQueries(api.ideas.get, [idea._id]);
        api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
          ...prev,
          ideas: (prev.ideas || []).map((idea) => {
            const updatedIdea = assessorIdeas.find((i) => i._id === idea._id);
            if (!updatedIdea) return idea;
            return { ...idea, assessments: updatedIdea.assessments };
          }),
        }));
        toast.success("Assessors updated");
      },
      (err) => toast.error(err.message),
    );
  };

  const removeAssessor = (
    assessment: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number],
  ) => {
    removeAssessors([idea._id], [assessment?.user], selectedAssessmentRoundId);
  };

  const removeInvite = (invitation: OpenAPI.Schemas["Invitation"]) => {
    util
      .confirm(
        t("ideas.invitations.remove.title"),
        t("ideas.invitations.remove.info_assessor"),
      )
      .then(() => {
        api.invitations.remove(
          invitation._id,
          () => {
            api.setQueryData(
              api.invitations.getForType,
              ["ideaAssessor", idea.challenge],
              (prev) => ({
                ...prev,
                invitations: prev.invitations.filter(
                  (i) => i._id !== invitation._id,
                ),
              }),
            );

            toast.success("Invite removed");
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const unsubmitAssessment = (
    assessment: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number],
  ) => {
    api.ideas.unsubmitAssessment(
      idea._id,
      selectedAssessmentRoundId,
      assessment.user,
      () => {
        toast.success("Assessment unsubmitted");
        api.invalidateQueries(api.ideas.get, [idea._id]);
        api.invalidateQueries(api.challenges.getIdeas, [idea.challenge]);
      },
      (err) => toast.error(err.message),
    );
  };

  const ideaId = idea._id;
  const invited = useMemo(
    () =>
      invitationQuery.data?.invitations
        .filter((i) => i.forIdeas?.includes(ideaId))
        .map((i) => ({
          ...i,
          profile: {
            firstName: i.inviteeUser?.profile?.firstName || i.invitee,
            created: `Invited ${formatDate(i.createdAt, "dd/MM/yy, HH:mm")}`,
          },
        })),
    [invitationQuery.data, ideaId],
  );

  if (assessmentRounds.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="simplydo-idea-assessment-report"
      style={{ maxHeight: fixedHeight || "auto", overflow: "auto" }}
    >
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={assessmentRounds.findIndex(
          (r) => r.id === selectedAssessmentRoundId,
        )}
        onTabChange={(_, data) => {
          const round = assessmentRounds[(data.activeIndex as number) || 0];
          if (!round) return;
          setSelectedAssessmentRoundId(round.id);
          setSelectedAssessor("");
        }}
        panes={assessmentRounds.map((round) => {
          const ideaScores = idea?.assessmentDetails?.assessmentRounds?.find(
            (r) => r.id === round.id,
          );
          const sentFeedback = idea?.assessmentFeedback?.find(
            (f) => f.assessmentRoundId === round.id,
          );
          const roundAssessments =
            idea?.assessments?.filter(
              (a) =>
                (a.assessmentRoundId || "legacy") === (round.id || "legacy"),
            ) || [];

          return {
            menuItem: (
              <Menu.Item>
                {round.name}{" "}
                <Label size="mini">{roundAssessments.length}</Label>
              </Menu.Item>
            ),
            render: () => (
              <div key={round.id}>
                {sentFeedback ?
                  <div style={{ marginBottom: 12 }}>
                    <Message size="tiny">
                      <Icon name="check circle" />
                      Feedback sent at{" "}
                      {formatDate(sentFeedback.createdAt, "dd/MM/yy, HH:mm")}
                    </Message>
                    <BasicAccordion
                      title="View sent feedback"
                      content={
                        <Segment>
                          <RichTextViewer value={sentFeedback.feedback || ""} />
                        </Segment>
                      }
                    />
                  </div>
                : null}
                <h4>
                  Total assessment score
                  <Label style={{ marginLeft: 10 }} color="violet">
                    {Math.round(ideaScores?.averageSubmittedScore || 0)}%
                  </Label>
                </h4>
                <Accordion>
                  {roundAssessments.map((a) => {
                    const isSelected = selectedAssessor === a.user;
                    const assessorUser = a.ownerUser;
                    return (
                      <>
                        <Accordion.Title
                          active={isSelected}
                          onClick={() =>
                            setSelectedAssessor(isSelected ? "" : a.user)
                          }
                        >
                          <Icon
                            name={isSelected ? "chevron down" : "chevron right"}
                          />
                          {assessorUser ?
                            <UserChip
                              user={assessorUser}
                              onClick={
                                isSelected ? undefined : (
                                  () => setSelectedAssessor(assessorUser._id)
                                )
                              }
                            />
                          : <span>Unknown user</span>}
                          {a.conflictOfInterest?.hasConflictOfInterest ?
                            <Label
                              size="small"
                              color="red"
                              style={{ marginLeft: 10, marginTop: 5 }}
                            >
                              <Icon name="warning sign" />
                              Conflict of interest
                            </Label>
                          : null}
                          {!a.conflictOfInterest?.hasConflictOfInterest && (
                            <Label
                              size="small"
                              color="violet"
                              style={{ marginLeft: 10, marginTop: 5 }}
                            >
                              {Math.round(a.assessmentScore || 0)}%
                            </Label>
                          )}
                          {a.isSubmitted ?
                            <Label
                              size="small"
                              color="green"
                              style={{ marginLeft: 10, marginTop: 5 }}
                            >
                              Submitted
                            </Label>
                          : <Label
                              size="small"
                              color="orange"
                              style={{ marginLeft: 10, marginTop: 5 }}
                            >
                              Not yet submitted
                            </Label>
                          }
                        </Accordion.Title>
                        <Accordion.Content active={isSelected}>
                          {(
                            a.conflictOfInterest?.hasConflictOfInterest &&
                            a.conflictOfInterest?.notes
                          ) ?
                            <Message
                              error
                              size="tiny"
                              header="Conflict of interest note"
                              content={a.conflictOfInterest.notes}
                            />
                          : Object.entries(a.assessment || {}).map(
                              ([fieldId, fieldAssessment]) => (
                                <Segment attached key={fieldId}>
                                  <h4 style={{ marginBottom: 0 }}>
                                    {getAssessmentTitle(fieldId)}{" "}
                                    {fieldAssessment.score ?
                                      <Label>
                                        Score: {fieldAssessment.score || 0}
                                      </Label>
                                    : null}
                                  </h4>
                                  <MultiLineParagraph>
                                    {fieldAssessment.notes || (
                                      <i>No comments left</i>
                                    )}
                                  </MultiLineParagraph>
                                </Segment>
                              ),
                            )
                          }
                          {(
                            util.isEmpty(a.assessment) &&
                            !a.conflictOfInterest?.hasConflictOfInterest
                          ) ?
                            <Message size="tiny" style={{ marginBottom: 12 }}>
                              This assessor has yet to begin their assessment.
                            </Message>
                          : null}
                          {(
                            util.canManageChallenge(
                              user,
                              idea.ownerChallenge,
                            ) ||
                            util.hasPermission(
                              user,
                              "challenge.editAssessments",
                              idea.challenge,
                            )
                          ) ?
                            <div
                              style={{
                                marginTop: 5,
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: 5,
                              }}
                            >
                              <Button
                                basic
                                size="mini"
                                style={{ marginBottom: 10 }}
                                onClick={() => removeAssessor(a)}
                                icon="trash"
                                content="Remove assessor"
                              />
                              {a.isSubmitted ?
                                <Button
                                  basic
                                  size="mini"
                                  style={{ marginBottom: 10, marginLeft: 5 }}
                                  onClick={() => unsubmitAssessment(a)}
                                  icon="ban"
                                  content="Unsubmit assessment"
                                />
                              : null}
                            </div>
                          : null}
                        </Accordion.Content>
                      </>
                    );
                  })}
                </Accordion>
              </div>
            ),
          };
        })}
      />
      {invited && invited.length > 0 ?
        <>
          <Divider section />
          <h5 style={{ marginRight: 16 }}>Pending invitations</h5>
          <Table basic="very" relaxed compact="very">
            <Table.Body>
              {invited.map((a) => {
                const assignedRoundIds = a.ideaDetails?.[idea._id]
                  ?.assessmentRounds || ["legacy"];
                const assignedRounds = assignedRoundIds.map(
                  (id) =>
                    assessmentRounds.find((r) => r.id === id)?.name ||
                    "Unknown round",
                );

                return (
                  <Table.Row key={a._id}>
                    <Table.Cell>
                      {a.inviteeUser?.profile?.firstName || a.invitee}
                    </Table.Cell>
                    <Table.Cell>{`${assignedRounds.join(", ")}`}</Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      {(
                        util.canManageChallenge(user, idea.ownerChallenge) ||
                        util.hasPermission(
                          user,
                          "challenge.editAssessments",
                          idea.challenge,
                        )
                      ) ?
                        <Button
                          size="tiny"
                          basic
                          icon="trash"
                          onClick={() => removeInvite(a)}
                        />
                      : null}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </>
      : null}
    </div>
  );
};

export default IdeaAssessmentReport;
